// Step 3 — Target: one process, or route each load by a value.
import { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { ArrowRight2 } from 'iconsax-react';

import type { IntegrationProcess, TargetSpec } from '../../types';
import type { StepProps } from '../stepProps';
import { CatalogueFieldPicker, FillBar, MONO, PanelHead, SourceName } from './SharedParts';

const SKIP = '__skip';

type RouteRow = { match: string; teamUid: string | null; inSpec: boolean; index: number; count: number | null };

function FlowPreview({ process, aljexCount }: { process: IntegrationProcess; aljexCount: number }) {
  const first = process.firstStep;
  if (!first) {
    return <Alert severity="warning">{process.name} has no first step with fields, so there is nothing to fill from Aljex.</Alert>;
  }
  const rest = process.steps.filter((s) => s.uid !== first.uid);
  const shown = rest.slice(0, 4);
  const total = first.fields.length;
  const opsCount = Math.max(0, total - aljexCount);
  const box = (key: string, title: string, meta: string, tone: 'auto' | 'next' | 'rest') => (
    <Paper
      key={key}
      variant="outlined"
      sx={{
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        minWidth: 150,
        borderColor: tone === 'auto' ? 'success.main' : 'divider',
        bgcolor: tone === 'auto' ? 'success.lighter' : tone === 'rest' ? 'action.hover' : 'background.paper'
      }}
    >
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="caption" color="text.secondary">
        {meta}
      </Typography>
    </Paper>
  );
  const arrow = (k: string) => (
    <Box key={k} sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }} aria-hidden>
      <ArrowRight2 size={16} />
    </Box>
  );
  const items: JSX.Element[] = [box(first.uid, first.name, 'Completed automatically', 'auto')];
  shown.forEach((s, i) => {
    items.push(arrow(`a${i}`));
    items.push(box(s.uid, s.name, i === 0 ? 'Next step' : 'Unchanged', i === 0 ? 'next' : 'rest'));
  });
  if (rest.length > shown.length) {
    items.push(arrow('amore'));
    items.push(box('more', `+${rest.length - shown.length} more`, 'Rest of the workflow', 'rest'));
  }
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="stretch" flexWrap="wrap" useFlexGap aria-label="Workflow of the chosen process">
        {items}
      </Stack>
      {total > 0 && opsCount > 0 && (
        <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
          <strong>Recommended: split the first step.</strong> Aljex can fill {aljexCount} of {first.name}&apos;s {total} fields. Moving the
          other {opsCount} into an <b>Operations details</b> step for the Operations role gives every field one owner, and nobody is handed
          a half-filled form.
        </Alert>
      )}
      {total > 0 && opsCount === 0 && (
        <Alert severity="success" icon={false}>
          Aljex fills every field of <b>{first.name}</b>, so the step can be completed automatically as it is.
        </Alert>
      )}
    </Stack>
  );
}

export default function TargetStep(props: StepProps) {
  const { spec, onChange, processes, catalogue, pipeline } = props;
  const target = spec.target;
  const setTarget = (t: TargetSpec) => onChange({ ...spec, target: t });

  const switchMode = (mode: 'SINGLE' | 'ROUTED') => {
    if (mode === target.mode) return;
    if (mode === 'SINGLE') {
      const firstRouted = target.mode === 'ROUTED' ? target.routes.find((r) => !!r.teamUid)?.teamUid : null;
      setTarget({ mode: 'SINGLE', teamUid: firstRouted || processes[0]?.uid || '' });
    } else {
      // Prefill from the last test's route breakdown; unrouted values still show as "Not routed" rows.
      const seen = pipeline.lastTest?.byRoute || [];
      setTarget({
        mode: 'ROUTED',
        routeByField: '',
        routes: seen.filter((r) => !!r.teamUid).map((r) => ({ match: r.value, teamUid: r.teamUid })),
        otherwise: 'SKIP'
      });
    }
  };

  const singleProcess = target.mode === 'SINGLE' ? processes.find((p) => p.uid === target.teamUid) || null : null;
  const aljexCount = useMemo(() => {
    if (!singleProcess?.firstStep) return 0;
    const uids = new Set(singleProcess.firstStep.fields.map((f) => f.uid));
    return spec.bindings.filter((b) => uids.has(b.fieldUid) && b.owner === 'SOURCE' && !!b.expr).length;
  }, [singleProcess, spec.bindings]);

  // Routed table: the spec's routes plus any value the last test saw that has no route yet.
  const rows: RouteRow[] = useMemo(() => {
    if (target.mode !== 'ROUTED') return [];
    const counts = new Map<string, number>();
    (pipeline.lastTest?.byRoute || []).forEach((r) => counts.set(r.value, r.count));
    const out: RouteRow[] = target.routes.map((r, index) => ({
      match: r.match,
      teamUid: r.teamUid,
      inSpec: true,
      index,
      count: counts.has(r.match) ? (counts.get(r.match) as number) : null
    }));
    const known = new Set(target.routes.map((r) => r.match));
    (pipeline.lastTest?.byRoute || []).forEach((r) => {
      if (!known.has(r.value)) out.push({ match: r.value, teamUid: '', inSpec: false, index: -1, count: r.count });
    });
    return out;
  }, [target, pipeline.lastTest]);

  const routeByCat = target.mode === 'ROUTED' ? catalogue.find((c) => c.field === target.routeByField) : undefined;

  const setRoute = (row: RouteRow, patch: { match?: string; teamUid?: string | null }) => {
    if (target.mode !== 'ROUTED') return;
    const routes = [...target.routes];
    if (row.inSpec) routes[row.index] = { ...routes[row.index], ...patch };
    else routes.push({ match: row.match, teamUid: '', ...patch });
    setTarget({ ...target, routes });
  };
  const removeRoute = (row: RouteRow) => {
    if (target.mode !== 'ROUTED' || !row.inSpec) return;
    setTarget({ ...target, routes: target.routes.filter((_, i) => i !== row.index) });
  };

  return (
    <Stack spacing={3}>
      <PanelHead
        title="Where do matching loads go?"
        subtitle="Send every match to one process, or route each load by a value such as its pickup hub."
      />
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={target.mode}
        onChange={(_, v) => v && switchMode(v)}
        aria-label="Target mode"
        sx={{ alignSelf: 'flex-start' }}
      >
        <ToggleButton value="SINGLE" sx={{ px: 2 }}>
          One process
        </ToggleButton>
        <ToggleButton value="ROUTED" sx={{ px: 2 }}>
          Route by value
        </ToggleButton>
      </ToggleButtonGroup>

      {target.mode === 'SINGLE' && (
        <Stack spacing={2.5}>
          <FormControl size="small" sx={{ maxWidth: 420 }}>
            <InputLabel id="di-target-process">Process</InputLabel>
            <Select
              labelId="di-target-process"
              label="Process"
              value={processes.some((p) => p.uid === target.teamUid) ? target.teamUid : ''}
              onChange={(e) => setTarget({ mode: 'SINGLE', teamUid: String(e.target.value) })}
            >
              {processes.map((p) => (
                <MenuItem key={p.uid} value={p.uid}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!singleProcess && (
            <Alert severity="warning">
              {target.teamUid
                ? 'The chosen process no longer exists. Pick another one.'
                : 'Pick the process new transactions are created in.'}
            </Alert>
          )}
          {singleProcess && <FlowPreview process={singleProcess} aljexCount={aljexCount} />}
        </Stack>
      )}

      {target.mode === 'ROUTED' && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">Route by</Typography>
            <CatalogueFieldPicker
              catalogue={catalogue}
              value={target.routeByField}
              onChange={(field) => setTarget({ ...target, routeByField: field })}
              placeholder="e.g. ps_company_1"
              width={300}
            />
            {routeByCat && <FillBar pct={routeByCat.fillPct} />}
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Value</TableCell>
                  <TableCell align="right">Loads · last test</TableCell>
                  <TableCell>Send to</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No values yet. Run a test to list the values seen in recent loads, or add one with + Route.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row, i) => {
                  const selectValue = row.teamUid === null ? SKIP : row.teamUid || '';
                  const notRouted = row.teamUid !== null && !row.teamUid;
                  return (
                    <TableRow key={`${row.inSpec ? 's' : 't'}-${row.index}-${row.match}-${i}`}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {row.inSpec ? (
                            <TextField
                              size="small"
                              value={row.match}
                              onChange={(e) => setRoute(row, { match: e.target.value })}
                              placeholder="Value"
                              inputProps={{ 'aria-label': 'Route value', style: { fontFamily: MONO, fontSize: '0.85rem' } }}
                              sx={{ width: 170 }}
                            />
                          ) : (
                            <SourceName>{row.match || '—'}</SourceName>
                          )}
                          {notRouted && <Chip size="small" color="warning" variant="outlined" label="Not routed" />}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{row.count ?? '—'}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          displayEmpty
                          value={selectValue}
                          onChange={(e) => {
                            const v = String(e.target.value);
                            setRoute(row, { teamUid: v === SKIP ? null : v });
                          }}
                          sx={{ minWidth: 260 }}
                          inputProps={{ 'aria-label': `Send ${row.match || 'these'} loads to` }}
                        >
                          <MenuItem value="">
                            <em>Not routed</em>
                          </MenuItem>
                          {processes.map((p) => (
                            <MenuItem key={p.uid} value={p.uid}>
                              {p.name}
                            </MenuItem>
                          ))}
                          <MenuItem value={SKIP}>Skip</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {row.inSpec && (
                          <Button size="small" color="secondary" onClick={() => removeRoute(row)} aria-label={`Remove route ${row.match}`}>
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setTarget({ ...target, routes: [...target.routes, { match: '', teamUid: '' }] })}
            >
              + Route
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Values seen in the last test are listed here. A new value shows up here and is skipped until someone routes it. Each process
            gets its own mapping in the next step.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
